<div class="admin-events-view">
  @if (error()) { <div class="alert alert-danger" role="alert">{{ error() }}</div> }
  @if (message()) { <div class="alert alert-success" role="status">{{ message() }}</div> }

  <div class="header-row">
    <div>
      <h2>Events</h2>
      <p>Create events for the right audience, confirm the signups, track attendance on the day and
        close the event to produce the attendee register. Reminders go out to confirmed attendees
        before the event starts.</p>
    </div>
    <div class="header-actions">
      <button type="button" class="btn btn-primary btn-sm" (click)="openCreate()" [disabled]="busy()">
        <i class="fas fa-plus"></i> New event
      </button>
    </div>
  </div>

  @if (showForm()) {
    <div class="card">
      <div class="card-header">{{ editingId() ? 'Edit event' : 'Create event' }}</div>
      <div class="card-body">
        <form class="event-form" [formGroup]="form" (ngSubmit)="save()">
          <div class="form-grid">
            <label>
              Title *
              <input class="form-control" formControlName="title" maxlength="200" />
            </label>
            <label>
              Type
              <select class="form-control" formControlName="eventType">
                @for (type of eventTypes; track type) {
                  <option [value]="type">{{ typeLabel(type) }}</option>
                }
              </select>
            </label>
            <label>
              Delivery
              <select class="form-control" formControlName="deliveryMode">
                @for (mode of deliveryModes; track mode) {
                  <option [value]="mode">{{ mode }}</option>
                }
              </select>
            </label>
            <label>
              Audience
              <select class="form-control" formControlName="targetAudience">
                @for (audience of audiences; track audience) {
                  <option [value]="audience">{{ audienceLabel(audience) }}</option>
                }
              </select>
            </label>
            @if (form.controls.targetAudience.value === 'SCHOLARSHIP_AWARDEES') {
              <label>
                Scholarship *
                <select class="form-control" formControlName="scholarshipId">
                  <option [ngValue]="null">Select a scholarship</option>
                  @for (scholarship of scholarships(); track scholarship.id) {
                    <option [ngValue]="scholarship.id">{{ scholarship.title }}</option>
                  }
                </select>
              </label>
              <label>
                Award cycle (optional)
                <input class="form-control" formControlName="cycle" placeholder="e.g. 2026" />
              </label>
            }
            <label>
              Starts *
              <input type="datetime-local" class="form-control" formControlName="startDate" />
            </label>
            <label>
              Ends *
              <input type="datetime-local" class="form-control" formControlName="endDate" />
            </label>
            <label>
              Registration opens
              <input type="datetime-local" class="form-control" formControlName="registrationOpenDate" />
            </label>
            <label>
              Registration closes
              <input type="datetime-local" class="form-control" formControlName="registrationCloseDate" />
            </label>
            <label>
              Venue
              <input class="form-control" formControlName="location" />
            </label>
            <label>
              Meeting link
              <input class="form-control" formControlName="meetingLink" placeholder="Confirmed attendees only" />
            </label>
            <label>
              Capacity
              <input type="number" min="0" class="form-control" formControlName="capacity" placeholder="Unlimited" />
            </label>
            <label>
              Reminder (hours before)
              <input type="number" min="1" class="form-control" formControlName="reminderHoursBefore" />
            </label>
          </div>

          <label class="full-width">
            Description
            <textarea class="form-control" rows="3" formControlName="description"></textarea>
          </label>

          <label class="full-width">
            Internal notes
            <textarea class="form-control" rows="2" formControlName="notes"></textarea>
          </label>

          <div class="checkbox-row">
            <label class="checkbox-line">
              <input type="checkbox" formControlName="isMandatory" />
              <span>Mandatory — every eligible awardee is registered automatically</span>
            </label>
            <label class="checkbox-line">
              <input type="checkbox" formControlName="allowPublicSignup" />
              <span>Allow email-only public signups (public events only)</span>
            </label>
            <label class="checkbox-line">
              <input type="checkbox" formControlName="publish" />
              <span>Publish immediately</span>
            </label>
          </div>

          <div class="sessions">
            <div class="sessions-header">
              <span>Attendance options (leave empty for a single occurrence)</span>
              <button type="button" class="btn btn-secondary btn-sm" (click)="addSession()">Add option</button>
            </div>
            <div formArrayName="sessions">
              @for (session of sessions.controls; track $index; let index = $index) {
                <div class="session-row" [formGroupName]="index">
                  <input class="form-control" formControlName="title" placeholder="Day / option name" />
                  <input type="datetime-local" class="form-control" formControlName="startDate" />
                  <input type="datetime-local" class="form-control" formControlName="endDate" />
                  <input class="form-control" formControlName="location" placeholder="Venue" />
                  <input class="form-control" formControlName="meetingLink" placeholder="Meeting link" />
                  <button type="button" class="btn btn-danger btn-sm" (click)="removeSession(index)">Remove</button>
                </div>
              }
            </div>
          </div>

          <div class="form-actions">
            <button type="submit" class="btn btn-primary" [disabled]="busy()">
              {{ editingId() ? 'Save changes' : 'Create event' }}
            </button>
            <button type="button" class="btn btn-secondary" (click)="cancelForm()">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  }

  <div class="tabs">
    <button type="button" class="tab-btn" [class.active]="scope() === 'upcoming'"
      (click)="selectScope('upcoming')">Upcoming</button>
    <button type="button" class="tab-btn" [class.active]="scope() === 'past'"
      (click)="selectScope('past')">Past &amp; closed</button>
    <button type="button" class="tab-btn" [class.active]="scope() === 'all'"
      (click)="selectScope('all')">All</button>
  </div>

  <div class="card">
    <div class="card-header">Filter Events</div>
    <div class="card-body">
      <form class="toolbar" (ngSubmit)="load()">
        <label>
          Status
          <select class="form-control" [value]="statusFilter()"
            (change)="statusFilter.set($any($event.target).value)">
            <option value="">All</option>
            @for (status of statuses; track status) {
              <option [value]="status">{{ status }}</option>
            }
          </select>
        </label>
        <label>
          Audience
          <select class="form-control" [value]="audienceFilter()"
            (change)="audienceFilter.set($any($event.target).value)">
            <option value="">All</option>
            @for (audience of audiences; track audience) {
              <option [value]="audience">{{ audienceLabel(audience) }}</option>
            }
          </select>
        </label>
        <label>
          Search
          <input class="form-control" [value]="search()" (input)="search.set($any($event.target).value)"
            placeholder="Title, reference, venue" />
        </label>
        <button type="submit" class="btn btn-primary btn-sm"><i class="fas fa-search"></i> Apply</button>
      </form>
    </div>
  </div>

  <div class="card">
    <div class="card-header">
      Events <span class="status status-booked">{{ events().length }}</span>
    </div>
    <div class="card-body" style="padding: 0;">
      <table class="table events-table">
        <thead>
          <tr>
            <th>Reference</th>
            <th>Event</th>
            <th>When</th>
            <th>Audience</th>
            <th>Signups</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          @if (loading()) {
            <tr><td colspan="7" class="empty-cell">Loading events…</td></tr>
          } @else if (events().length === 0) {
            <tr>
              <td colspan="7" class="empty-cell">
                No events in this view match the current filters.
                @if (scope() === 'past') {
                  <div>Finished events, and any published event whose date has passed, appear here for reconciliation.</div>
                }
              </td>
            </tr>
          }
          @for (event of events(); track event.id) {
            <tr>
              <td>{{ event.referenceNumber }}</td>
              <td>
                <strong>{{ event.title }}</strong>
                <div class="muted">
                  {{ typeLabel(event.eventType) }}
                  @if (event.isMandatory) { · <span class="status status-rejected">Mandatory</span> }
                  @if (event.sessionCount > 0) { · {{ event.sessionCount }} option(s) }
                </div>
              </td>
              <td>{{ event.startDate | date: 'yyyy-MM-dd HH:mm' }}</td>
              <td>
                {{ audienceLabel(event.targetAudience) }}
                @if (event.scholarshipTitle) { <div class="muted">{{ event.scholarshipTitle }}</div> }
              </td>
              <td>
                {{ event.registeredCount }} registered · {{ event.confirmedCount }} confirmed
                @if (event.attendedCount > 0) { · {{ event.attendedCount }} attended }
              </td>
              <td><span [class]="statusClass(event.status)">{{ event.status }}</span></td>
              <td class="row-actions">
                <button type="button" class="btn btn-secondary btn-sm" (click)="open(event)"><i class="fas fa-eye"></i> Open</button>
                <button type="button" class="btn btn-secondary btn-sm" (click)="openEdit(event)"><i class="fas fa-edit"></i> Edit</button>
                @if (canPublish(event)) {
                  <button type="button" class="btn btn-primary btn-sm" (click)="changeStatus(event, 'Publish')"><i class="fas fa-upload"></i> Publish</button>
                }
                @if (canClose(event)) {
                  <button type="button" class="btn btn-secondary btn-sm" (click)="changeStatus(event, 'Close')"><i class="fas fa-lock"></i> Close</button>
                }
                @if (event.status !== 'CANCELLED' && event.status !== 'CLOSED') {
                  <button type="button" class="btn btn-danger btn-sm" (click)="changeStatus(event, 'Cancel')"><i class="fas fa-times"></i> Cancel</button>
                }
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </div>

  @if (selected(); as event) {
    <div class="card selected-event">
      <div class="card-header">
        {{ event.title }}
        <span [class]="statusClass(event.status)">{{ event.status }}</span>
        <button type="button" class="btn btn-secondary btn-sm panel-close" (click)="closeEvent()">Close</button>
      </div>
      <div class="card-body">
        <div class="event-summary">
          <div><span class="muted">Reference</span><strong>{{ event.referenceNumber }}</strong></div>
          <div>
            <span class="muted">When</span>
            <strong>{{ event.startDate | date: 'yyyy-MM-dd HH:mm' }} → {{ event.endDate | date: 'yyyy-MM-dd HH:mm' }}</strong>
          </div>
          <div><span class="muted">Venue</span><strong>{{ event.location || '—' }}</strong></div>
          <div>
            <span class="muted">Signups</span>
            <strong>{{ event.registeredCount }} ({{ event.confirmedCount }} confirmed, {{ event.attendedCount }} attended)</strong>
          </div>
          <div><span class="muted">Reminder</span><strong>{{ event.reminderHoursBefore }}h before start</strong></div>
          @if (event.meetingLink) {
            <div><span class="muted">Meeting link</span><strong><a [href]="event.meetingLink" target="_blank" rel="noopener">{{ event.meetingLink }}</a></strong></div>
          }
        </div>

        <div class="tabs">
          <button type="button" class="tab-btn" [class.active]="tab() === 'registrations'"
            (click)="tab.set('registrations')">Registrations</button>
          <button type="button" class="tab-btn" [class.active]="tab() === 'attendance'"
            (click)="tab.set('attendance')">Attendance</button>
          <button type="button" class="tab-btn" [class.active]="tab() === 'report'"
            (click)="tab.set('report')">Report &amp; correspondence</button>
        </div>

        @if (tab() === 'registrations') {
          <div class="toolbar bulk-bar">
            <label class="checkbox-line">
              <input type="checkbox" (change)="toggleAll()" />
              <span>Select pending ({{ chosen().size }} selected)</span>
            </label>
            <label class="inline-field">
              Rejection reason
              <input class="form-control" [value]="rejectReason()"
                (input)="rejectReason.set($any($event.target).value)" placeholder="Required when rejecting" />
            </label>
            <div class="bulk-actions">
              <button type="button" class="btn btn-primary btn-sm" (click)="bulkDecide('Approve')" [disabled]="busy()">
                <i class="fas fa-check"></i> Approve selected
              </button>
              <button type="button" class="btn btn-danger btn-sm" (click)="bulkDecide('Reject')" [disabled]="busy()">
                <i class="fas fa-times"></i> Reject selected
              </button>
            </div>
          </div>

          <table class="table">
            <thead>
              <tr>
                <th><span class="sr-only">Select</span></th>
                <th>Attendee</th>
                <th>Contact</th>
                <th>Option</th>
                <th>Registered</th>
                <th>Status</th>
                <th>Attendance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              @if (registrations().length === 0) {
                <tr><td colspan="8" class="empty-cell">No registrations yet.</td></tr>
              }
              @for (registration of registrations(); track registration.id) {
                <tr>
                  <td>
                    @if (isPending(registration)) {
                      <input type="checkbox" [checked]="chosen().has(registration.id)"
                        (change)="toggleChosen(registration)" />
                    }
                  </td>
                  <td>
                    {{ registration.fullName || 'Unknown attendee' }}
                    @if (registration.isAutoRegistered) { <div class="muted">auto-registered</div> }
                    @if (registration.isPublicSignup) { <div class="muted">public signup</div> }
                  </td>
                  <td>{{ registration.emailAddress }}</td>
                  <td>{{ registration.sessionTitle || '—' }}</td>
                  <td>{{ registration.registeredDate | date: 'yyyy-MM-dd' }}</td>
                  <td>
                    <span [class]="registrationClass(registration.status)">{{ registration.status }}</span>
                    @if (registration.rejectionReason) {
                      <div class="muted">{{ registration.rejectionReason }}</div>
                    }
                  </td>
                  <td>
                    @if (registration.attendanceStatus) {
                      <span [class]="attendanceClass(registration.attendanceStatus)">{{ registration.attendanceStatus }}</span>
                    } @else {
                      <span class="muted">Not recorded</span>
                    }
                  </td>
                  <td class="row-actions">
                    @if (isPending(registration)) {
                      <button type="button" class="btn btn-primary btn-sm" (click)="decide(registration, 'Approve')"
                        [disabled]="busy()"><i class="fas fa-check"></i> Confirm</button>
                      <button type="button" class="btn btn-danger btn-sm" (click)="decide(registration, 'Reject')"
                        [disabled]="busy()"><i class="fas fa-times"></i> Reject</button>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }

        @if (tab() === 'attendance') {
          <div class="toolbar bulk-bar">
            <label class="btn btn-secondary btn-sm file-button">
              <i class="fas fa-file-upload"></i> Upload attendance sheet (CSV)
              <input type="file" accept=".csv,text/csv" (change)="onSheetFile($event)" hidden />
            </label>
            <span class="muted">
              Rows list the email address of each person who attended. Anyone confirmed but missing from
              the sheet is recorded as absent.
            </span>
          </div>

          @if (importResult(); as result) {
            <div class="alert alert-success" role="status">
              {{ result.markedPresent }} present, {{ result.markedAbsent }} absent, {{ result.skipped }} skipped.
              @if (result.errors.length > 0) {
                <ul>
                  @for (error of result.errors; track error.lineNumber) {
                    <li>Line {{ error.lineNumber }}: {{ error.message }}</li>
                  }
                </ul>
              }
            </div>
          }

          <table class="table">
            <thead>
              <tr>
                <th>Attendee</th>
                <th>Contact</th>
                <th>Option</th>
                <th>Attendance</th>
                <th>Signed in</th>
                <th>Marked</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              @if (attendance().length === 0) {
                <tr><td colspan="7" class="empty-cell">No confirmed attendees yet.</td></tr>
              }
              @for (row of attendance(); track row.registrationId) {
                <tr>
                  <td>{{ row.fullName || 'Unknown attendee' }}</td>
                  <td>{{ row.emailAddress }}</td>
                  <td>{{ row.sessionTitle || '—' }}</td>
                  <td>
                    @if (row.status) {
                      <span [class]="attendanceClass(row.status)">{{ row.status }}</span>
                    } @else {
                      <span class="muted">Not recorded</span>
                    }
                    @if (row.signedInOnline) { <div class="muted">signed in online</div> }
                  </td>
                  <td>{{ row.signedInDate ? (row.signedInDate | date: 'yyyy-MM-dd HH:mm') : '—' }}</td>
                  <td>{{ row.markedDate ? (row.markedDate | date: 'yyyy-MM-dd HH:mm') : '—' }}</td>
                  <td class="row-actions">
                    <button type="button" class="btn btn-primary btn-sm" (click)="mark(row, 'PRESENT')" [disabled]="busy()"><i class="fas fa-check"></i> Present</button>
                    <button type="button" class="btn btn-danger btn-sm" (click)="mark(row, 'ABSENT')" [disabled]="busy()"><i class="fas fa-user-slash"></i> Absent</button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }

        @if (tab() === 'report') {
          <div class="toolbar bulk-bar">
            <button type="button" class="btn btn-secondary btn-sm" (click)="exportReport()" [disabled]="busy()">
              <i class="fas fa-download"></i> Export attendee register (CSV)
            </button>
            <span class="muted">{{ report().length }} row(s) on the register.</span>
          </div>

          <table class="table">
            <thead>
              <tr>
                <th>Attendee</th>
                <th>Email</th>
                <th>Option</th>
                <th>Registration</th>
                <th>Attendance</th>
                <th>Signed in</th>
              </tr>
            </thead>
            <tbody>
              @if (report().length === 0) {
                <tr><td colspan="6" class="empty-cell">Nothing on the register yet.</td></tr>
              }
              @for (row of report(); track row.registrationId) {
                <tr>
                  <td>{{ row.attendeeName }}</td>
                  <td>{{ row.attendeeEmail }}</td>
                  <td>{{ row.session || '—' }}</td>
                  <td><span [class]="registrationClass(row.registrationStatus)">{{ row.registrationStatus }}</span></td>
                  <td>
                    @if (row.attendanceStatus) {
                      <span [class]="attendanceClass(row.attendanceStatus)">{{ row.attendanceStatus }}</span>
                    } @else {
                      <span class="muted">Not recorded</span>
                    }
                  </td>
                  <td>{{ row.signedInDate ? (row.signedInDate | date: 'yyyy-MM-dd HH:mm') : '—' }}</td>
                </tr>
              }
            </tbody>
          </table>

          <form class="correspondence-form" [formGroup]="correspondence" (ngSubmit)="sendCorrespondence()">
            <h3>Send correspondence</h3>
            <div class="form-grid">
              <label>
                Audience
                <select class="form-control" formControlName="audience">
                  <option value="CONFIRMED">All confirmed attendees</option>
                  <option value="ATTENDEES">People who attended</option>
                  <option value="NO_SHOWS">People who did not attend</option>
                </select>
              </label>
              <label>
                Subject
                <input class="form-control" formControlName="subject" />
              </label>
            </div>
            <label class="full-width">
              Message
              <textarea class="form-control" rows="4" formControlName="body"></textarea>
            </label>
            <div class="form-actions">
              <button type="submit" class="btn btn-primary" [disabled]="busy()"><i class="fas fa-paper-plane"></i> Send</button>
            </div>
          </form>
        }
      </div>
    </div>
  }
</div>
